import { QBox } from '@qualio/ui-components';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { useEffect, useRef, useState } from 'react';
import { Page } from 'react-pdf';
import { useIntersectionObserver } from '../../../../../hooks/useIntersectionObserver';

const A4_PAGE_WIDTH = 794;
// ISO 216 A4 height/width ratio, used to size the placeholder until the real page dimensions are known
const A4_ASPECT_RATIO = 297 / 210;
const RENDER_ROOT_MARGIN = '1000px 0px';

const PDFPageWrapper = ({
  zoom,
  pageNumber,
  pdfDocument,
}: {
  zoom: number;
  pageNumber: number;
  pdfDocument?: PDFDocumentProxy;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isVisible = useIntersectionObserver(containerRef, {
    rootMargin: RENDER_ROOT_MARGIN,
  });
  const [aspectRatio, setAspectRatio] = useState(A4_ASPECT_RATIO);

  useEffect(() => {
    let cancelled = false;

    pdfDocument
      ?.getPage(pageNumber)
      .then((page) => {
        if (cancelled) {
          return;
        }
        const { width, height } = page.getViewport({ scale: 1 });
        setAspectRatio(height / width);
      })
      .catch(() => {
        // Keep the A4 fallback if the page dimensions can't be determined
      });

    return () => {
      cancelled = true;
    };
  }, [pdfDocument, pageNumber]);

  const placeholderHeight = A4_PAGE_WIDTH * zoom * aspectRatio;

  return (
    <QBox ref={containerRef} style={{ minHeight: placeholderHeight }}>
      {isVisible && (
        <>
          <QBox border="1px" borderColor="gray.200" boxShadow="md">
            <Page
              pageNumber={pageNumber}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              scale={zoom}
              width={A4_PAGE_WIDTH}
            />
          </QBox>
          <QBox height="5px" width="100%" />
        </>
      )}
    </QBox>
  );
};

export const PDFPageV2 = ({
  zoom,
  pageCount,
  pageNumber,
  isPaginated,
  pdfDocument,
}: {
  zoom: number;
  pageCount: number;
  pageNumber: number;
  isPaginated: boolean;
  pdfDocument?: PDFDocumentProxy;
}) => {
  if (!isPaginated) {
    return (
      <QBox>
        {Array.from({ length: pageCount }).map((_, idx) => (
          <PDFPageWrapper
            key={`page-${idx + 1}`}
            zoom={zoom}
            pageNumber={idx + 1}
            pdfDocument={pdfDocument}
          />
        ))}
      </QBox>
    );
  }

  return (
    <PDFPageWrapper
      zoom={zoom}
      pageNumber={pageNumber}
      pdfDocument={pdfDocument}
    />
  );
};
